// src/contexts/FeatureFlagContext.jsx
//
// ADMIN-NAV-FEATURE-TOGGLES: React context that delivers live feature
// flag values fetched from GET /api/feature-flags/nav at app boot.
//
// ADMIN-NAV-TOGGLES-V2: Exposes updateFlag(key, patch) so NavFeaturesPanel
// can mutate the shared flag map immediately on toggle — causing NavRail to
// re-render instantly without a fetch or page reload. Also exposes nav_order
// (ordered array of left_nav keys) so NavRail can sort ALL_NAV_ITEMS correctly.
//
// LOCATION-TABS-CONTROL: Exposes locationTabs (ordered array of location_tab
// flag objects) so LocationDetail can drive its tab strip from DB-persisted
// flags. updateLocationTab(key, enabled) and updateLocationTabOrder(orderedKeys)
// allow NavFeaturesPanel to reflect changes optimistically.
//
// ADMIN-ADD-LEASES-TABS: Exposes leaseTabs (ordered array of lease_tab flag
// objects) so LeaseDetailPage can drive its tab strip from DB-persisted flags.
// updateLeaseTab(key, enabled) and updateLeaseTabOrder(orderedKeys) allow
// NavFeaturesPanel to reflect changes optimistically.
//
// ADMIN-REPORTS-VISIBILITY-ORDER: Exposes reportFlags (ordered array of report
// flag objects) so ReportsPage can drive its tile order and visibility.
// updateReportFlag(key, enabled) and updateReportFlagOrder(orderedKeys)
// allow NavFeaturesPanel to reflect changes optimistically.
//
// Usage:
//   - Wrap the tree: <FeatureFlagProvider>...</FeatureFlagProvider>
//   - Read flags:    const { flags, updateFlag, nav_order, locationTabs, leaseTabs, reportFlags } = useFeatureFlagContext();
//   - Compat hook:   const flags = useFeatureFlags();  (reads flags only)
//
// In tests, vi.mock("../config/featureFlags.js") replaces useFeatureFlags
// so the context provider fetch is not exercised.
//
// DEFAULT_FLAGS mirrors the current static featureFlags.js values so that
// a render cycle before the fetch resolves shows sensible defaults (no flash
// of incorrectly visible/hidden items).

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { log } from "../utils/logger.js";
import { dbNavKey } from "../app/governedNav.js";
import { DEFAULT_PROJECTS_TABS, normalizeProjectsTabs } from "../app/projectsTabs.js";
import { DEFAULT_PLACES_TABS } from "../app/places.js";

// Canonical default flag state.
// Mirror of the 42P01 fallback in server/routes/featureFlags.js and
// RESTORE_DEFAULTS_NAV in server/routes/admin.js — keep all three in sync.
export const DEFAULT_FLAGS = {
  FEATURE_PLACES_NAV: true,
  projects_risk_register: false,
  FEATURE_PULSE_NAV:         true,
  FEATURE_LOCATIONS_NAV:     true,
  // NAV-RESTRUCTURE-RADAR: Radar and Playbooks are new top-level nav items, default ON.
  FEATURE_RADAR_NAV:         false,
  FEATURE_PLAYBOOKS_NAV:     false,
  FEATURE_PROJECTS_NAV:      true,
  FEATURE_PLAN_NAV:          true,
  FEATURE_ASK_PORTFOLIO_NAV: true,
  FEATURE_ACTIONS_BELL:      false,
  FEATURE_REPORTS_NAV:       true,
  FEATURE_LEASES_NAV:        true,
  FEATURE_SIGNALS_NAV:       false,
  FEATURE_ROADMAP_NAV:       false,
  FEATURE_WORKFLOWS_NAV:     false,
  FEATURE_REVIEWS_NAV:       false,
  FEATURE_OKRS_NAV:          false,
  FEATURE_IN_FOCUS_PANEL:    true,
  IN_FOCUS_LAYOUT:           'classic',
  ATTENDANCE_SOURCE:          'alta_verkada',
  preview_mode:              false,
  score_ai_recs:             false,
  pulse_data_confidence:     false,
  pdf_abstraction:           false,
  scoring_model_archived:    false,
  // Projects intel is a governance kill switch; it is always opt-in.
  projects_intel:            false,
  // PRE-LAUNCH-PREVIEW: enabled by default until the configured launch date.
  pre_launch_preview:        true,
  // RADAR-PHASE-1-SPINE: three-band Radar page powered by focus_items engine.
  // Default false — shipped dark until emitters are validated.
  FEATURE_RADAR_SPINE:       false,
  FEATURE_RADAR_DECISIONS_REGISTER: true,
  // Markets Strategy and Guidance remain hidden until explicitly enabled.
  FEATURE_MARKETS_STRATEGY_TABS: false,
};

// Named constant for consumers — import this instead of using a magic string.
// Mirrors the DB key 'in_focus_panel' via KEY_TO_CLIENT_FLAG in featureFlags.js.
export const FEATURE_IN_FOCUS_PANEL = "FEATURE_IN_FOCUS_PANEL";

// A1-ARCHIVE-LOCATION-SCORING: scoring model archived flag key.
// When flags[FEATURE_SCORING_MODEL_ARCHIVED] === true, all per-object score UI
// is hidden and recompute paths are blocked server-side.
export const FEATURE_SCORING_MODEL_ARCHIVED = "scoring_model_archived";

// RADAR-PHASE-1-SPINE: feature flag key for the new three-band Radar page.
export const FEATURE_RADAR_SPINE = "FEATURE_RADAR_SPINE";

// GOVERN-DECISIONS-REGISTER: Radar-owned visibility gate for the embedded
// Decisions register and its canonical deep link.
export const FEATURE_RADAR_DECISIONS_REGISTER = "FEATURE_RADAR_DECISIONS_REGISTER";

// Runtime gate for the Markets Strategy and Guidance tabs; defaults dark.
export const FEATURE_MARKETS_STRATEGY_TABS = "FEATURE_MARKETS_STRATEGY_TABS";

// IN_FOCUS_LAYOUT — string value for the 'pulse.in_focus_layout' DB flag.
// One of: 'classic' | 'rich' | 'off'.  Defaults to 'classic'.
export const IN_FOCUS_LAYOUT = "IN_FOCUS_LAYOUT";

// Canonical fallback nav_order. OKRs remain governed by the persisted order
// (including the admin preview contract), but stay out of the fallback order
// until governance rows are available. The public endpoint remains authoritative.
export const DEFAULT_NAV_ORDER = [
  "home", "places", "leases", "projects", "reports",
];

// The ordered, governed contract returned by /api/feature-flags/nav. Keep the
// row metadata separate from nav_order's legacy string[] compatibility shape.
export const DEFAULT_NAV_CONFIG = DEFAULT_NAV_ORDER.map((key, sortOrder) => ({
  key,
  label: null,
  enabled: true,
  sortOrder,
  navGroup: null,
}));

// Default report flags: all core reports enabled in canonical order.
// Used before the server response arrives (or when migration 0134 hasn't run).
// Mirrors RESTORE_DEFAULTS_REPORTS in server/routes/admin.js.
export const DEFAULT_REPORT_FLAGS = [
  { key: "report-headcount-trend", enabled: true, sort_order: 1 },
  { key: "report-open-roles",      enabled: true, sort_order: 2 },
  { key: "report-space-fit",       enabled: true, sort_order: 3 },
  { key: "report-foot-traffic",    enabled: true, sort_order: 4 },
  { key: "report-utilization",     enabled: true, sort_order: 5 },
  { key: "report-cost-leakage",    enabled: true, sort_order: 6 },
  { key: "report-data-coverage",   enabled: true, sort_order: 7 },
];

// Default location_tabs: 6 surviving tabs enabled in canonical order.
// Used before the server response arrives (or when migration 0403 hasn't run).
// Mirrors RESTORE_DEFAULTS_TABS in server/routes/admin.js.
// tab_score, tab_lease, tab_cost removed by O-38 (migration 0403).
export const DEFAULT_LOCATION_TABS = [
  { key: "tab_overview",    enabled: true, sort_order: 0 },
  { key: "tab_utilization", enabled: true, sort_order: 1 },
  { key: "tab_experience",  enabled: true, sort_order: 2 },
  { key: "tab_service",     enabled: true, sort_order: 3 },
  { key: "tab_intel",       enabled: true, sort_order: 4 },
];

// Default lease_tabs: all 6 tabs enabled in canonical order.
// Used before the server response arrives (or when migration 0360 hasn't run).
// Mirrors RESTORE_DEFAULTS_LEASE_TABS in server/routes/admin.js.
export const DEFAULT_LEASE_TABS = [
  { key: "lease_tab_overview",   enabled: true, sort_order: 0 },
  { key: "lease_tab_financials", enabled: true, sort_order: 1 },
  { key: "lease_tab_dates",      enabled: true, sort_order: 2 },
  { key: "lease_tab_documents",  enabled: true, sort_order: 3 },
  { key: "lease_tab_intel",      enabled: true, sort_order: 4 },
];

// Default admin_sections: canonical Admin sidebar section flags. Signal &
// noise is intentionally shipped off until its Radar surface is enabled.
// order. Used before the server response arrives (or when migration 0302
// hasn't run). Mirrors RESTORE_DEFAULTS_ADMIN_SECTIONS in server/routes/admin.js
// and ADMIN_SECTIONS in src/features/admin/AdminPage.jsx.
export const DEFAULT_ADMIN_SECTIONS = [
  { key: "users_sso",            enabled: true, locked: true,  sort_order: 0 },
  { key: "roles",                enabled: true, locked: true,  sort_order: 1 },
  { key: "nav_features",         enabled: true, locked: true,  sort_order: 2 },
  { key: "app_branding",         enabled: true, locked: false, sort_order: 3 },
  { key: "ai_governance",        enabled: true, locked: false, sort_order: 4 },
  { key: "radar_signal_noise",   enabled: false, locked: false, sort_order: 5 },
  { key: "data",                 enabled: true, locked: false, sort_order: 5 },
  { key: "integrations",         enabled: true, locked: false, sort_order: 6 },
  { key: "notifications_slack",  enabled: true, locked: false, sort_order: 7 },
  { key: "amenities",            enabled: true, locked: false, sort_order: 8 },
  { key: "currency",             enabled: true, locked: false, sort_order: 9 },
  { key: "benchmarks",           enabled: true, locked: false, sort_order: 10 },
  { key: "non_working_days",     enabled: true, locked: false, sort_order: 15 },
  { key: "retention_privacy",    enabled: true, locked: false, sort_order: 11 },
  { key: "markets_location_ids", enabled: true, locked: false, sort_order: 12 },
  { key: "audit",                enabled: true, locked: true,  sort_order: 13 },
  { key: "activity",             enabled: true, locked: true,  sort_order: 17 },
];

// Context value shape:
//   {
//     flags: object,
//     flagsReady: boolean,   // false until the first server fetch resolves
//     nav_order: string[],
//     nav_labels: { [navKey: string]: string },  // DB labels keyed by NavRail item key
//     nav_groups: { [navKey: string]: string },  // G-32: DB group overrides keyed by nav item key
//     locationTabs: { key, enabled, sort_order }[],
//     leaseTabs: { key, enabled, sort_order }[],
//     reportFlags: { key, enabled, sort_order }[],
//     updateFlag: fn,
//     updateNavOrder: fn,
//     updateNavGroups: fn,   // G-32: optimistic group update after cross-group drag
//     updateLocationTab: fn,
//     updateLocationTabOrder: fn,
//     updateLeaseTab: fn,
//     updateLeaseTabOrder: fn,
//     updateReportFlag: fn,
//     updateReportFlagOrder: fn,
//     refreshFromServer: fn,
//   }
const FeatureFlagContext = createContext({
  placesTabs: DEFAULT_PLACES_TABS,
  updatePlacesTab: () => {},
  flags:                  DEFAULT_FLAGS,
  flagsReady:             false,
  nav_order:              DEFAULT_NAV_ORDER,
  nav_config:             DEFAULT_NAV_CONFIG,
  nav_labels:             {},
  nav_groups:             {},
  locationTabs:           DEFAULT_LOCATION_TABS,
  leaseTabs:              DEFAULT_LEASE_TABS,
  projectsTabs:           DEFAULT_PROJECTS_TABS,
  updateProjectsTab:      () => {},
  updateProjectsTabOrder: () => {},
  reportFlags:            DEFAULT_REPORT_FLAGS,
  reportAccess:           {},
  adminSections:          DEFAULT_ADMIN_SECTIONS,
  updateFlag:             () => {},
  updateNavOrder:         () => {},
  updateNavGroups:        () => {},
  updateLocationTab:      () => {},
  updateLocationTabOrder: () => {},
  updateLeaseTab:         () => {},
  updateLeaseTabOrder:    () => {},
  updateReportFlag:       () => {},
  updateReportFlagOrder:  () => {},
  updateAdminSection:     () => {},
  refreshFromServer:      () => {},
});

/**
 * Wraps the app tree and fetches live flag values from the server at boot.
 * Falls back to DEFAULT_FLAGS on any fetch/parse error (non-blocking).
 *
 * @param {{ children: React.ReactNode, initialFlags?: object }} props
 */
export function FeatureFlagProvider({ children, initialFlags }) {
  const [flags,        setFlags]        = useState(initialFlags ?? DEFAULT_FLAGS);
  // Start ready immediately when initialFlags are explicitly supplied (e.g. in tests).
  // In normal app boot, initialFlags is undefined so flagsReady starts false and
  // transitions to true after the async /api/feature-flags/nav fetch resolves.
  const [flagsReady,   setFlagsReady]   = useState(initialFlags !== undefined);
  const [navOrder,     setNavOrder]     = useState(DEFAULT_NAV_ORDER);
  const [navConfig,    setNavConfig]    = useState(DEFAULT_NAV_CONFIG);
  const [navLabels,    setNavLabels]    = useState({});
  // G-32: nav_groups maps nav item key → group string from DB; overrides the
  // static ALL_NAV_ITEMS group field in filterNavItems so NavRail dividers
  // reflect cross-group drag assignments without a page reload.
  const [navGroups,    setNavGroups]    = useState({});
  const [locationTabs, setLocationTabs] = useState(DEFAULT_LOCATION_TABS);
  const [leaseTabs,    setLeaseTabs]    = useState(DEFAULT_LEASE_TABS);
  const [projectsTabs, setProjectsTabs] = useState(DEFAULT_PROJECTS_TABS);
  const [reportFlags,  setReportFlags]  = useState(DEFAULT_REPORT_FLAGS);
  const [reportAccess, setReportAccess] = useState({});
  const [adminSections, setAdminSections] = useState(DEFAULT_ADMIN_SECTIONS);

  /**
   * Parse the nav_order field from the server response.
   * Server now returns {key, label, navGroup}[] so we can apply DB labels and
   * group overrides in the client. Falls back gracefully to string[].
   * @param {(string | {key: string, label: string | null, navGroup?: string | null})[]} order
   * @returns {{ keys: string[], labels: Record<string, string>, groups: Record<string, string>, config: object[] }}
   */
  function parseNavOrder(order) {
    const keys   = [];
    const labels = {};
    const groups = {};
    const config = [];
    for (const item of order) {
      if (typeof item === "string") {
        keys.push(item);
        config.push({ key: item, label: null, enabled: undefined, sortOrder: config.length, navGroup: null });
      } else if (item && typeof item === "object") {
        keys.push(item.key);
        if (item.label)    labels[item.key] = item.label;
        // G-32: navGroup from DB overrides the static ALL_NAV_ITEMS group field.
        if (item.navGroup) groups[item.key] = item.navGroup;
        config.push({
          key: item.key,
          label: item.label ?? null,
          enabled: item.enabled,
          sortOrder: item.sortOrder ?? item.sort_order ?? config.length,
          navGroup: item.navGroup ?? item.nav_group ?? null,
        });
      }
    }
    return { keys, labels, groups, config };
  }

  useEffect(() => {
    let cancelled = false;
    fetch("/api/feature-flags/nav")
      .then((r) => {
        if (!r.ok) throw new Error(`/api/feature-flags/nav responded ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (data && typeof data === "object") {
          const { nav_order: order, location_tabs: tabs, lease_tabs: ltabs, project_tabs: ptabs, reports: rpts, admin_sections: sections, report_access: access, ...flagMap } = data;
          setPlacesTabs(Array.isArray(data.places_tabs) ? data.places_tabs : DEFAULT_PLACES_TABS);
          setProjectsTabs(normalizeProjectsTabs(ptabs, (message) => log.warn(message)));
          setReportAccess(access ?? {});
          setFlags(flagMap);
          if (Array.isArray(order)) {
            if (order.length === 0) {
              log.warn("FeatureFlagProvider: empty governed collection", { collection: "nav_order" });
            }
            const { keys, labels, groups, config } = parseNavOrder(order);
            setNavOrder(keys);
            setNavConfig(config);
            setNavLabels(labels);
            // G-32: replace DB-sourced group overrides, rather than merging
            // stale values from a previous configuration into a refresh.
            setNavGroups(groups);
          }
          if (Array.isArray(tabs)) {
            if (tabs.length === 0) {
              log.warn("FeatureFlagProvider: empty governed collection", { collection: "location_tabs" });
            }
            setLocationTabs(tabs);
          }
          if (Array.isArray(ltabs)) {
            if (ltabs.length === 0) {
              log.warn("FeatureFlagProvider: empty governed collection", { collection: "lease_tabs" });
            }
            setLeaseTabs(ltabs);
          }
          if (Array.isArray(rpts)) {
            if (rpts.length === 0) {
              log.warn("FeatureFlagProvider: empty governed collection", { collection: "reports" });
            }
            setReportFlags(rpts);
          }
          if (Array.isArray(sections)) {
            if (sections.length === 0) {
              log.warn("FeatureFlagProvider: empty governed collection", { collection: "admin_sections" });
            }
            setAdminSections(sections);
          }
        }
        setFlagsReady(true);
      })
      .catch((err) => {
        log.warn("FeatureFlagProvider: fetch failed - using defaults", { err: String(err) });
        // Still mark ready on error so the nav renders with DEFAULT_FLAGS
        // rather than staying blank indefinitely.
        setFlagsReady(true);
      });
    return () => { cancelled = true; };
  }, []);

  /**
   * Optimistically mutate one flag key in the shared context.
   * NavFeaturesPanel calls this on every toggle so NavRail re-renders instantly.
   *
   * @param {string} key       - DB key, e.g. "locations"
   * @param {object} patch     - Partial flag map update, e.g. { FEATURE_PLAN_NAV: true }
   */
  const updateFlag = useCallback((key, patch) => {
    setFlags((prev) => ({ ...prev, ...patch }));
  }, []);

  const [placesTabs, setPlacesTabs] = useState(DEFAULT_PLACES_TABS);
  const updatePlacesTab = useCallback((key, enabled) => {
    setPlacesTabs((prev) => prev.map((row) => row.key === key ? { ...row, enabled } : row));
    setFlags((prev) => ({ ...prev, places_tabs: (prev.places_tabs ?? DEFAULT_PLACES_TABS)
      .map((row) => row.key === key ? { ...row, enabled } : row) }));
  }, []);

  /**
   * Optimistically replace the nav_order in the shared context.
   * NavFeaturesPanel calls this immediately after a drag-drop so NavRail
   * re-renders with the new order without waiting for the server round-trip.
   *
   * @param {string[]} newOrder - Ordered array of left_nav keys.
   */
  const updateNavOrder = useCallback((newOrder) => {
    setNavOrder(newOrder);
    setNavConfig((prev) => {
      const byKey = Object.fromEntries(prev.map((entry) => [entry.key, entry]));
      return newOrder.map((key, sortOrder) => ({
        ...(byKey[key] ?? { key, label: null, enabled: undefined, navGroup: null }),
        key,
        sortOrder,
      }));
    });
  }, []);

  /**
   * Optimistically update one governed left-nav row. The DB key and the rail
   * key differ for Offices (`locations` → `portfolio`), so this is the one
   * authoritative translation point used by Admin and both rails.
   */
  const updateNavItem = useCallback((dbKey, enabled) => {
    const navKey = dbNavKey(dbKey);
    setNavConfig((prev) => prev.map((entry) =>
      entry.key === navKey ? { ...entry, enabled } : entry
    ));
  }, []);

  /**
   * G-32: Optimistically merge nav_group overrides into the shared context.
   * NavFeaturesPanel calls this after a cross-group drag so NavRail dividers
   * re-render immediately with the new group assignment.
   *
   * @param {Record<string, string>} groupPatch - Partial { [navKey]: navGroup } map to merge.
   */
  const updateNavGroups = useCallback((groupPatch) => {
    setNavGroups((prev) => {
      const next = { ...prev };
      for (const [key, group] of Object.entries(groupPatch)) {
        if (group == null) delete next[key];
        else next[key] = group;
      }
      return next;
    });
  }, []);

  /**
   * Optimistically update a single location_tab flag's enabled state.
   * NavFeaturesPanel calls this on every location_tab toggle so LocationDetail
   * re-renders instantly without a fetch.
   *
   * @param {string}  key     - DB key, e.g. "tab_overview"
   * @param {boolean} enabled - New enabled state.
   */
  const updateLocationTab = useCallback((key, enabled) => {
    setLocationTabs((prev) =>
      prev.map((t) => t.key === key ? { ...t, enabled } : t)
    );
  }, []);

  /**
   * Optimistically replace the location_tab order in the shared context.
   * NavFeaturesPanel calls this immediately after a drag-drop so LocationDetail
   * re-renders with the new tab order.
   *
   * @param {string[]} orderedKeys - Ordered array of location_tab DB keys.
   */
  const updateLocationTabOrder = useCallback((orderedKeys) => {
    setLocationTabs((prev) => {
      const byKey = Object.fromEntries(prev.map((t) => [t.key, t]));
      return orderedKeys
        .map((k, idx) => byKey[k] ? { ...byKey[k], sort_order: idx } : null)
        .filter(Boolean);
    });
  }, []);

  /**
   * Optimistically update a single lease_tab flag's enabled state.
   * NavFeaturesPanel calls this on every lease_tab toggle so LeaseDetailPage
   * re-renders instantly without a fetch.
   *
   * @param {string}  key     - DB key, e.g. "lease_tab_overview"
   * @param {boolean} enabled - New enabled state.
   */
  const updateLeaseTab = useCallback((key, enabled) => {
    setLeaseTabs((prev) =>
      prev.map((t) => t.key === key ? { ...t, enabled } : t)
    );
  }, []);

  const updateProjectsTab = useCallback((key, enabled) => {
    setProjectsTabs((prev) => normalizeProjectsTabs(prev.map((row) =>
      row.key === key ? { ...row, enabled } : row)));
  }, []);

  const updateProjectsTabOrder = useCallback((keys) => {
    setProjectsTabs((prev) => normalizeProjectsTabs(prev.map((row) => ({
      ...row, sort_order: keys.includes(row.key) ? keys.indexOf(row.key) : row.sort_order,
    }))));
  }, []);

  /**
   * Optimistically replace the lease_tab order in the shared context.
   * NavFeaturesPanel calls this immediately after a drag-drop so LeaseDetailPage
   * re-renders with the new tab order.
   *
   * @param {string[]} orderedKeys - Ordered array of lease_tab DB keys.
   */
  const updateLeaseTabOrder = useCallback((orderedKeys) => {
    setLeaseTabs((prev) => {
      const byKey = Object.fromEntries(prev.map((t) => [t.key, t]));
      return orderedKeys
        .map((k, idx) => byKey[k] ? { ...byKey[k], sort_order: idx } : null)
        .filter(Boolean);
    });
  }, []);

  /**
   * Optimistically update a single report flag's enabled state.
   * NavFeaturesPanel calls this on every report toggle so ReportsPage
   * re-renders instantly without a fetch.
   *
   * If the key is not yet present in reportFlags (e.g. when the server
   * returned an empty reports array or the key was absent from defaults),
   * this upserts a new entry so the Reports page always reacts to admin
   * toggles even on first toggle after a stale initial state.
   *
   * @param {string}  key     - DB key, e.g. "report-space-fit"
   * @param {boolean} enabled - New enabled state.
   */
  const updateReportFlag = useCallback((key, enabled) => {
    setReportFlags((prev) => {
      const exists = prev.some((r) => r.key === key);
      if (exists) {
        return prev.map((r) => r.key === key ? { ...r, enabled } : r);
      }
      // Key absent — append a new entry so the Reports page reacts immediately.
      const maxOrder = prev.reduce((m, r) => Math.max(m, r.sort_order ?? 0), 0);
      return [...prev, { key, enabled, sort_order: maxOrder + 1 }];
    });
  }, []);

  /**
   * Optimistically replace the report flag order in the shared context.
   * NavFeaturesPanel calls this immediately after a drag-drop so ReportsPage
   * re-renders with the new report order.
   *
   * @param {string[]} orderedKeys - Ordered array of report flag DB keys.
   */
  const updateReportFlagOrder = useCallback((orderedKeys) => {
    setReportFlags((prev) => {
      const byKey = Object.fromEntries(prev.map((r) => [r.key, r]));
      return orderedKeys
        .map((k, idx) => byKey[k] ? { ...byKey[k], sort_order: idx + 1 } : null)
        .filter(Boolean);
    });
  }, []);

  /**
   * Optimistically update a single admin_section flag's enabled state.
   * NavFeaturesPanel calls this on every admin-section toggle so
   * AdminSubSidebar re-renders instantly without a fetch.
   *
   * @param {string}  key     - DB key, e.g. "scoring"
   * @param {boolean} enabled - New enabled state.
   */
  const updateAdminSection = useCallback((key, enabled) => {
    setAdminSections((prev) =>
      prev.map((s) => s.key === key ? { ...s, enabled } : s)
    );
  }, []);

  /**
   * Re-fetches /api/feature-flags/nav and syncs all context state.
   * Call this after any server-side bulk change (e.g. restore-defaults)
   * so NavRail and LocationDetail reflect the canonical state without
   * requiring a full page reload.
   */
  const refreshFromServer = useCallback(() => {
    fetch("/api/feature-flags/nav")
      .then((r) => {
        if (!r.ok) throw new Error(`/api/feature-flags/nav responded ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (data && typeof data === "object") {
          const { nav_order: order, location_tabs: tabs, lease_tabs: ltabs, project_tabs: ptabs, reports: rpts, admin_sections: sections, report_access: access, ...flagMap } = data;
          if (Object.hasOwn(data, "project_tabs")) {
            setProjectsTabs(normalizeProjectsTabs(ptabs, (message) => log.warn(message)));
          }
          if (Array.isArray(data.places_tabs)) setPlacesTabs(data.places_tabs);
          setReportAccess(access ?? {});
          setFlags(flagMap);
          if (Array.isArray(order)) {
            if (order.length === 0) {
              log.warn("FeatureFlagProvider: empty governed collection", { collection: "nav_order" });
            }
            const { keys, labels, groups, config } = parseNavOrder(order);
            setNavOrder(keys);
            setNavConfig(config);
            setNavLabels(labels);
             setNavGroups(groups);
          }
          if (Array.isArray(tabs)) {
            if (tabs.length === 0) {
              log.warn("FeatureFlagProvider: empty governed collection", { collection: "location_tabs" });
            }
            setLocationTabs(tabs);
          }
          if (Array.isArray(ltabs)) {
            if (ltabs.length === 0) {
              log.warn("FeatureFlagProvider: empty governed collection", { collection: "lease_tabs" });
            }
            setLeaseTabs(ltabs);
          }
          if (Array.isArray(rpts)) {
            if (rpts.length === 0) {
              log.warn("FeatureFlagProvider: empty governed collection", { collection: "reports" });
            }
            setReportFlags(rpts);
          }
          if (Array.isArray(sections)) {
            if (sections.length === 0) {
              log.warn("FeatureFlagProvider: empty governed collection", { collection: "admin_sections" });
            }
            setAdminSections(sections);
          }
        }
      })
      .catch((err) => {
        log.warn("FeatureFlagProvider: refresh failed", { err: String(err) });
      });
  }, []);

  // Reuse the public refresh path on return and at bounded intervals. Some
  // browsers keep secondary windows visible without delivering a focus event.
  useEffect(() => {
    const refresh = () => refreshFromServer();
    const onVisibility = () => { if (document.visibilityState !== "hidden") refresh(); };
    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refreshFromServer]);

  const value = {
    placesTabs,
    updatePlacesTab,
    flags,
    flagsReady,
    nav_order:  navOrder,
    nav_config: navConfig,
    nav_labels: navLabels,
    // G-32: DB-sourced group overrides keyed by nav item key.
    nav_groups: navGroups,
    locationTabs,
    leaseTabs,
    projectsTabs,
    updateProjectsTab,
    updateProjectsTabOrder,
    reportFlags,
    reportAccess,
    adminSections,
    updateFlag,
    updateNavOrder,
    updateNavItem,
    updateNavGroups,
    updateLocationTab,
    updateLocationTabOrder,
    updateLeaseTab,
    updateLeaseTabOrder,
    updateReportFlag,
    updateReportFlagOrder,
    updateAdminSection,
    refreshFromServer,
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

/**
 * Returns the full context value.
 * Use this in NavFeaturesPanel (needs update fns) and LocationDetail (needs locationTabs).
 */
export function useFeatureFlagContext() {
  return useContext(FeatureFlagContext);
}

/**
 * Returns the current feature-flag map. Components that gate nav items
 * or view renders on feature flags call this hook.
 *
 * This is the backward-compatible hook — it returns only the flags map,
 * not nav_order, locationTabs, or update functions. Existing consumers are unchanged.
 *
 * @returns {typeof DEFAULT_FLAGS}
 */
export function useFeatureFlags() {
  return useContext(FeatureFlagContext).flags;
}
